import type { NFastCreationModal } from '@app/components/FastCreationModal/FastCreationModal.types';
import type {
  NAccessRights,
  NAnalytics,
  NAuth,
  NBranches,
  NEmployees,
  NPositions,
  NServices,
  NSmallGlobalEntities,
  NUtils,
} from '@app/types';

import type { FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';

export namespace NUsers {
  export type User = {
    dateBirth: string | null;
    email: string | null;
    password: string | null;
    gender: string | null;
    id: number;
    isAdmin: boolean;
    isDeleted: boolean;
    nameFirst: string;
    nameLast: string | null;
    nameMiddle: string | null;
    phone: string;
    photo: string;
    position: string | null;
    userBranchIds: number[];
    positionId: number;
    description: string | null;
    note: string | null;
    isTerminated: boolean | null;
    isEmployee: boolean | null;
    dateWorkStart?: string | null;
    datePatentEnd?: string | null;
    citizenship?: string | null;
    passportInfo?: string | null;
    taxPayerId?: number | null;
    insuranceCertificateN?: string | null;
    isSaveAdditions: boolean;
    phoneCountryId: number | null;
    isFakeEmail: boolean;
    isFakePhone: boolean;
    phoneCode: NAuth.User['telCode'];
    alterIsEmployee: boolean | null;
    isAi: boolean;
    snils: string | null;
    signerRole: string | null;
    specialityCodeNsi: string | null;
    positionCode: string | null;
    telegramId: string | null;
  };

  export type UserServerFormat = {
    DateBirth: string | null;
    Email: string | null;
    Gender: string | null;
    Id: number;
    IsAdmin: boolean;
    IsDeleted: boolean;
    NameFirst: string;
    NameLast: string | null;
    NameMiddle: string | null;
    Phone: string;
    Photo: string;
    Position: string | null;
    UserBranchIds: number[];
  };

  export type GeneralFormValues = {
    firstName: string;
    lastName: string;
    middleName: string | undefined | null;
    email: string | undefined | null;
    password: string | undefined | null;
    phone: string | undefined;
    position: number | undefined;
    gender: string | number | undefined;
    isAdmin: boolean | undefined;
    dateBirth: Dayjs | string;
    isDeleted: boolean | undefined;
    description: string | undefined | null;
    alterIsEmployee: boolean | undefined | null;
    telegramId: string | undefined | null;
    TelCodeSelect: { label: string; value: number };
  };

  export type EditUserInfoProps = {
    user: User | undefined;
    setUser: (user: User) => void;
    open: boolean;
    setOpen: (status: boolean) => void;
    showUserModal: () => void;
    hideUserModal: () => void;
    serverError: string;
    allBranches: NBranches.ModelBranchItem[];
    positions: NSmallGlobalEntities.ModelItem[];
    setServerError: (error: string) => void;
    sendDataToDb: NServices.SendDataToDb<NUsers.ModelUser>;
    isLoader: boolean;
    setLoader: (status: boolean) => void;
    errorType: NUtils.ErrorTypes;
    telCode: string;
    modalForm: FormInstance<NFastCreationModal.FastCreationFormValues>;
    generalForm: FormInstance<GeneralFormValues>;
    isPositionsModalOpen: boolean;
    addPosition: () => void;
    handleOk: () => void;
    handleCancel: () => void;
    finishModal: NEmployees.FinishModal;
    hasUserRightToCreatePosition: boolean;
    phoneCountryId: number | null | undefined;
    setPhoneCountryId: (value: number | null | undefined) => void;
    confirmDeletingModalOpen: boolean;
    setConfirmDeletingModalOpen: (status: boolean) => void;
    accessRights: NAccessRights.ModelAccessRights;
    setAccessRights: React.Dispatch<
      React.SetStateAction<NAccessRights.ModelAccessRights | undefined>
    >;
    initialAccessRightData: NAccessRights.ModelAccessRights;
    setInitialAccessRightData: (value: NAccessRights.ModelAccessRights) => void;
    hasUserRightForModuleView: boolean;
    hasUserRightToViewAccessRightTab: boolean;
    hasUserRightToEdit: boolean;
    isNeedToUpdateUserPreview: boolean;
    setIsNeedToUpdateUserPreview: (args: boolean) => void;
    setErrorType: (value: NUtils.ErrorTypes) => void;
  };

  export type UpdateUserPayload = {
    Id: number;
    NameLast: string;
    NameFirst: string;
    NameMiddle: string;
    Gender: string | null;
    Email: string;
    Phone: string;
    PhoneCountryId: number;
    PhoneCode: string;
    PositionId: number | null;
    DateBirth: string | null;
    IsDeleted: boolean;
    IsAdmin: boolean;
    Photo: string;
    UserBranchIds: number[];
    Description: string;
    IsEmployee: boolean;
    AlterIsEmployee: boolean;
    DateWorkStart: string;
    DatePatentEnd: string;
    PassportInfo: string;
    TaxPayerId: number;
    InsuranceCertificateN: string;
    Citizenship: string;
    IsTerminated: boolean;
    IsSaveAdditions: boolean;
    MigrateId: number;
    IsFakeEmail: boolean;
    IsFakePhone: boolean;
  };

  export type GeneralInformationTab = {
    initUserData: User | undefined;
    onUserUpdate?: (user: User) => void;
  };

  export type UserProfileInfo = {
    ActiveBranch: string;
    DateBirth: string | null;
    Email: string;
    Gender: string;
    Id: number;
    NameFirst: string;
    NameLast: string;
    NameMiddle: string;
    Phone: string;
    Photo: string;
    PositionId: number | null;
    Timezone: string;
  };

  export type EditUserHistoryProps = {
    user: NUsers.User | undefined;
  };

  export type UserForActionsHistory = {
    Id: NUsers.User['id'];
    NameFirst: NUsers.User['nameFirst'];
    NameLast: NUsers.User['nameLast'];
    Email: NUsers.User['email'];
    Gender: NSmallGlobalEntities.Gender;
    Phone: NSmallGlobalEntities.Phone;
    PositionId: NPositions.ModelPosition['Id'];
    Position: NPositions.ModelPosition['Name'];
    BranchesS: NBranches.Branch[] | null;
    Photo: NUsers.User['photo'];
    IsAdmin: NAccessRights.ModelAccessRights['IsAdmin'];
    IsDeleted: NUsers.User['isDeleted'];
    LastEntry: string | null;
    LastEntryS: string | null;
    Description: string | null;
    AccessRightsDescription: string | null;
  };

  export type ModelUserSearchResult = {
    Id: number;
    Filter: ModelUserSearchFilter;
    Users: NAnalytics.ModelUserLite[] | null;
    PageN: number;
    CountPages: number;
    CountUsers: number;
  };

  export type ModelUserSearchFilter = {
    SearchTerm: string | null;
    BranchIds: number[] | null;
    PositionIds: number[] | null;
    Statuses: string[] | null;
    UsersPerPage: number;
  };

  export type ModelUser = {
    Id: number;
    NameLast: string | null;
    NameFirst: string | null;
    NameMiddle: string | null;
    Gender: NSmallGlobalEntities.Gender;
    Email: string | null;
    Password: string | null;
    Phone: string | null;
    PhoneCountryId: number | null;
    PhoneCode: string | null;
    PositionId: number | null;
    DateBirth: string | null;
    IsDeleted: boolean;
    IsAdmin: boolean;
    Photo: string | null;
    UserBranchIds: number[] | null;
    Description: string | null;
    IsEmployee: boolean;
    AlterIsEmployee: boolean | null;
    DateWorkStart: string | null;
    DatePatentEnd: string | null;
    PassportInfo: string | null;
    TaxPayerId: number | null;
    InsuranceCertificateN: string | null;
    Citizenship: string | null;
    IsTerminated: boolean;
    IsSaveAdditions: boolean;
    MigrateId: number | null;
    IsFakeEmail: boolean;
    IsFakePhone: boolean;
    Note: string | null;
    IsAi?: boolean;
    Snils: string | null;
    SignerRole: string | null;
    SpecialityCodeNsi: string | null;
    PositionCode: string | null;
    TelegramId: string | null;
  };

  export type ModelUserProfile = {
    Id: number;
    NameFirst: string | null;
    NameLast: string | null;
    NameMiddle: string | null;
    Gender: 'f' | 'm' | null;
    Email: string | null;
    Phone: string | null;
    PositionId: number | null;
    Position: string | null;
    DateBirth: string | null;
    IsDeleted: boolean;
    IsTerminated: boolean;
    IsAdmin: boolean;
    Photo: string | null;
    LastVisitS: string | null;
    LastVisitOs: string | null;
    Timezone: string | null;
    IsEmployee: boolean;
    DateWorkStart: string | null;
    DatePatentEnd: string | null;
    PassportInfo: string | null;
    TaxPayerId: number | null;
    InsuranceCertificateN: string | null;
    Citizenship: string | null;
    UserBranches: NBranches.ModelBranchLite[];
    IsFakeEmail: boolean;
    IsFakePhone: boolean;
    TelegramId: string | null;
  };

  export type ModelProfile = {
    Id: number;
    NameFirst: string | null;
    NameLast: string | null;
    NameMiddle: string | null;
    Gender: NSmallGlobalEntities.Gender;
    Email: string | null;
    Phone: string | null;
    PhoneCountryId: number | null;
    PositionId: number | null;
    DateBirth: string | null;
    Photo: string | null;
    Timezone: string | null;
    Organization: { Id: number; Name: string | null } | null;
  };

  export type ModelUserPassword = {
    UserId: number;
    Password: string | null;
  };

  export type ModelUserEmail = {
    UserId: number;
    Email: string | null;
  };
}
